/**
 * ComboSystem — shape-clear combo.
 *
 * Combo only advances when a full bloom/shape is cleared (via bus event
 * `bloom:cleared`). Eating individual food never advances combo — this
 * enforces the "sweep a whole shape in one pass" gameplay loop instead
 * of letting players keep combo alive by hunting random crumbs.
 *
 * Timer is generous (15s) so there's enough time between blooms for the
 * next shape to spawn and be reached.
 *
 * Milestones (in shape-clears):
 *   - 3:  chromatic aberration shimmer
 *   - 6:  time-slow flourish
 *   - 10: screen flash + bonus DNA drop
 */

import { bus } from '@/core/EventBus';
import { player } from './PlayerState';

export class ComboSystem {
  combo = 0;
  multiplier = 1;
  private timer = 0;
  private nextMilestoneIdx = 0;
  private milestones = [3, 6, 10];

  constructor() {
    bus.on('bloom:cleared', ({ tier }) => {
      if (tier === 'main') this.advance();
      else this.extendTimer();
    });
  }

  /** Satellite clears don't count toward combo but give breathing room. */
  private extendTimer(): void {
    if (this.combo > 0) this.timer = Math.min(6, this.timer + 2);
  }

  update(dt: number): void {
    if (this.combo === 0) return;
    this.timer -= dt;
    if (this.timer <= 0) this.reset();
  }

  /** No-op kept for AbsorptionSystem's call site; combo is shape-driven now. */
  registerAbsorption(): void { /* intentional no-op */ }

  private advance(): void {
    this.combo += 1;
    // Tight window: player must clear the next shape quickly or combo dies.
    // Shapes span the whole screen now, so this forces decisive pathing.
    this.timer = 6;
    // combo=1 → 1.8x, combo=3 → 2.6x, combo=6 → 3.25x, combo=10 → 3.77x, capped at 4x
    // Skill-dominant: combo multiplier is now the main biomass scaling vector
    // (biomass_mult upgrade was steepened and flat food values were reduced).
    this.multiplier = 1 + Math.min(3, Math.log2(this.combo + 1) * 0.8);
    bus.emit('combo:changed', { combo: this.combo, multiplier: this.multiplier });

    while (this.nextMilestoneIdx < this.milestones.length &&
           this.combo >= this.milestones[this.nextMilestoneIdx]) {
      this.handleMilestone(this.milestones[this.nextMilestoneIdx]);
      this.nextMilestoneIdx += 1;
    }
  }

  reset(): void {
    if (this.combo !== 0) {
      bus.emit('combo:reset', undefined);
    }
    this.combo = 0;
    this.multiplier = 1;
    this.nextMilestoneIdx = 0;
    bus.emit('combo:changed', { combo: 0, multiplier: 1 });
  }

  private handleMilestone(n: number): void {
    bus.emit('combo:milestone', { combo: n });
    if (n === 3) {
      bus.emit('audio:cue', { id: 'milestone', volume: 0.3 });
    } else if (n === 6) {
      bus.emit('fx:timeSlow', { factor: 0.5, duration: 0.5 });
      bus.emit('audio:cue', { id: 'milestone', volume: 0.4 });
    } else if (n === 10) {
      bus.emit('fx:flash', { color: 0xffffff, alpha: 0.3, duration: 0.2 });
      bus.emit('fx:screenShake', { intensity: 8, duration: 0.25 });
      player.addDna(2);
      bus.emit('audio:cue', { id: 'milestone', volume: 0.5 });
    }
  }
}
